/**
 * ═══════════════════════════════════════════════════════════════════════
 * NextPatientBanner — Per-Doctor "Send Next Patient" Banner
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Banners are DERIVED from dailyVisits — they survive refresh.
 *
 * For each doctor with an active/completed visit and a next QUEUE patient:
 *   → Banner: "Send Patient Y to Dr. X"
 *
 * Dismissal rules (banner won't show for that patient):
 *   - Receptionist clicks "Confirm Sent" → visit_id saved in sessionStorage
 *     as receptionist_confirmed metadata → banner gone
 *   - Receptionist Skip / Cancel → API changes visit status → banner gone
 *   - Doctor completes / skips visit → patient no longer QUEUE → banner
 *     shifts to next QUEUE patient automatically
 *   - Patient goes IN_PROGRESS → no longer QUEUE → next QUEUE shown
 *
 * receptionist_confirmed is VIEW-ONLY metadata, not a backend filter.
 * Only Smart View is affected — no impact on Manage Visits.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useRealtimeCallback } from '../../../hooks/useRealtimeEvent';
import { REALTIME_EVENTS } from '../../../services/realtime/RealtimeSyncService';
import { PhoneButton } from '../PhoneButton';
import { formatToken } from '../../../utils/formatToken';
import { sortByQueue } from '../../../utils/queueSort';

const CONFIRMED_KEY = 'ak_receptionist_confirmed';

/** Load confirmed visit IDs from sessionStorage */
function loadConfirmedVisits() {
  try {
    const raw = sessionStorage.getItem(CONFIRMED_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}

/** Persist confirmed visit IDs to sessionStorage */
function persistConfirmedVisits(set) {
  try {
    sessionStorage.setItem(CONFIRMED_KEY, JSON.stringify([...set]));
  } catch { /* non-critical */ }
}

export default function NextPatientBanner({ isDark, dailyVisits, onSkip, onCancel }) {
  // Lightweight set of visit_ids the receptionist has "confirmed sent"
  const [confirmedVisits, setConfirmedVisits] = useState(() => loadConfirmedVisits());
  const confirmedRef = useRef(confirmedVisits);

  // Realtime-driven override banners (for instant updates before next poll)
  const [realtimeBanners, setRealtimeBanners] = useState({});

  // Keep ref + sessionStorage in sync
  useEffect(() => {
    confirmedRef.current = confirmedVisits;
    persistConfirmedVisits(confirmedVisits);
  }, [confirmedVisits]);

  // ── Cleanup: prune confirmed IDs that are stale ──────────────────
  // Two conditions cause a confirmed ID to become stale:
  //   1. Visit moved past QUEUE (completed / skipped / cancelled)
  //   2. Doctor started a *different* patient — the confirmed patient
  //      is still QUEUE but the queue context changed, so the banner
  //      must re-appear for the correct next-in-line.
  useEffect(() => {
    if (!dailyVisits || dailyVisits.length === 0) return;
    setConfirmedVisits((prev) => {
      if (prev.size === 0) return prev;
      const visitStatuses = new Map(dailyVisits.map((v) => [v.visit_id, v.status]));

      // Build a set of visit_ids that are currently IN_PROGRESS, keyed by doctor
      const inProgressByDoctor = new Map();
      const visitDoctorMap = new Map();
      for (const v of dailyVisits) {
        visitDoctorMap.set(v.visit_id, v.doctor_member_id);
        if (v.status === 'IN_PROGRESS') {
          inProgressByDoctor.set(v.doctor_member_id, v.visit_id);
        }
      }

      let changed = false;
      const next = new Set(prev);
      for (const vid of next) {
        const status = visitStatuses.get(vid);
        // Condition 1: visit moved past QUEUE
        if (status && status !== 'QUEUE') {
          next.delete(vid);
          changed = true;
          continue;
        }
        // Condition 2: visit is still QUEUE but its doctor started
        // a different patient → stale confirmation, re-show banner
        if (status === 'QUEUE') {
          const doctorId = visitDoctorMap.get(vid);
          const activePat = doctorId && inProgressByDoctor.get(doctorId);
          if (activePat && activePat !== vid) {
            next.delete(vid);
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [dailyVisits]);

  // ── Cleanup realtime overrides when their visit resolves ─────────
  useEffect(() => {
    if (!dailyVisits || dailyVisits.length === 0) return;
    const visitMap = new Map(dailyVisits.map((v) => [v.visit_id, v.status]));
    setRealtimeBanners((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [doctorId, banner] of Object.entries(next)) {
        const status = visitMap.get(banner.nextVisitId);
        if (status && status !== 'QUEUE') {
          delete next[doctorId];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [dailyVisits]);

  // ── Derive banners from dailyVisits (source of truth on refresh) ─
  const derivedBanners = useMemo(() => {
    if (!dailyVisits || dailyVisits.length === 0) return {};

    // Group by doctor
    const byDoctor = {};
    for (const v of dailyVisits) {
      const did = v.doctor_member_id;
      if (!did) continue;
      if (!byDoctor[did]) byDoctor[did] = [];
      byDoctor[did].push(v);
    }

    const result = {};

    for (const [doctorId, visits] of Object.entries(byDoctor)) {
      // Interleave priority patients with walk-ins (each appointment skips one walk-in)
      const queueSorted = sortByQueue(visits.filter((v) => v.status === 'QUEUE'));
      const nonQueue = visits.filter((v) => v.status !== 'QUEUE')
        .sort((a, b) => (a.queue_number || 0) - (b.queue_number || 0));
      const sorted = [...nonQueue, ...queueSorted];

      const inProgress = sorted.find((v) => v.status === 'IN_PROGRESS');
      const hasCompleted = sorted.some(
        (v) => v.status === 'COMPLETED' || v.status === 'SKIPPED'
      );

      // First QUEUE patient NOT confirmed by receptionist
      const nextQueue = sorted.find(
        (v) => v.status === 'QUEUE' && !confirmedVisits.has(v.visit_id)
      );

      if (!nextQueue) continue;

      if (inProgress) {
        // Doctor seeing someone → next patient should get ready
        result[doctorId] = {
          type: 'started',
          doctorName: inProgress.doctor_name || 'Doctor',
          doctorMemberId: doctorId,
          currentPatient: inProgress.patient_name || 'Patient',
          currentQueueNumber: inProgress.queue_number,
          nextPatient: nextQueue.patient_name || 'Next Patient',
          nextQueueNumber: nextQueue.queue_number,
          nextAppointmentNumber: nextQueue.appointment_number || null,
          nextVisitId: nextQueue.visit_id,
          nextPatientMobile: nextQueue.patient_mobile || null,
        };
      } else if (hasCompleted) {
        // Doctor finished last patient → send next
        result[doctorId] = {
          type: 'ready',
          doctorName: sorted[0]?.doctor_name || 'Doctor',
          doctorMemberId: doctorId,
          nextPatient: nextQueue.patient_name || 'Next Patient',
          nextQueueNumber: nextQueue.queue_number,
          nextAppointmentNumber: nextQueue.appointment_number || null,
          nextVisitId: nextQueue.visit_id,
          nextPatientMobile: nextQueue.patient_mobile || null,
        };
      }
      // No IN_PROGRESS and no COMPLETED → doctor hasn't started → no banner
    }

    return result;
  }, [dailyVisits, confirmedVisits]);

  // ── Merge: realtime overrides take priority over derived ─────────
  const finalBanners = useMemo(() => {
    const merged = { ...derivedBanners };
    for (const [k, v] of Object.entries(realtimeBanners)) {
      if (!confirmedVisits.has(v.nextVisitId)) {
        merged[k] = v; // realtime has richer data (e.g. skippedPatient)
      }
    }
    // Final safety filter
    for (const k of Object.keys(merged)) {
      if (confirmedVisits.has(merged[k].nextVisitId)) {
        delete merged[k];
      }
    }
    return Object.entries(merged);
  }, [derivedBanners, realtimeBanners, confirmedVisits]);

  // ── Actions ──────────────────────────────────────────────────────
  const confirmSent = useCallback((doctorId, visitId) => {
    // Mark visit as receptionist_confirmed → removes banner
    setConfirmedVisits((prev) => {
      const next = new Set(prev);
      next.add(visitId);
      return next;
    });
    // Also clear any realtime override for this doctor
    setRealtimeBanners((prev) => {
      const next = { ...prev };
      delete next[doctorId];
      return next;
    });
  }, []);

  const addRealtimeBanner = useCallback((doctorId, bannerData) => {
    if (confirmedRef.current.has(bannerData.nextVisitId)) return;
    setRealtimeBanners((prev) => ({
      ...prev,
      [doctorId]: { ...bannerData, createdAt: Date.now() },
    }));
  }, []);

  const dismissRealtimeBanner = useCallback((doctorId) => {
    setRealtimeBanners((prev) => {
      const next = { ...prev };
      delete next[doctorId];
      return next;
    });
  }, []);

  // ── Helper: clear a visit_id from confirmedVisits so the server-
  //    computed nextInQueue can show through immediately. ───────────
  const unconfirmVisit = useCallback((visitId) => {
    setConfirmedVisits((prev) => {
      if (!prev.has(visitId)) return prev;
      const next = new Set(prev);
      next.delete(visitId);
      return next;
    });
  }, []);

  // ── Realtime listeners (instant, before next poll) ───────────────
  useRealtimeCallback(REALTIME_EVENTS.QUEUE_STARTED, (event) => {
    if (event.source === 'local') return;
    const { visit, nextInQueue, doctorMemberId, doctorName } = event.payload || {};
    if (!doctorMemberId || !nextInQueue) return;

    // Server says this is the correct next patient — clear any stale
    // confirmation so the banner can display it.
    unconfirmVisit(nextInQueue.visit_id);

    addRealtimeBanner(doctorMemberId, {
      type: 'started',
      doctorName: doctorName || visit?.doctor_name || 'Doctor',
      doctorMemberId,
      currentPatient: visit?.patient_name || 'Patient',
      currentQueueNumber: visit?.queue_number,
      nextPatient: nextInQueue.patient_name || 'Next Patient',
      nextQueueNumber: nextInQueue.queue_number,
      nextAppointmentNumber: nextInQueue.appointment_number || null,
      nextVisitId: nextInQueue.visit_id,
      nextPatientMobile: nextInQueue.patient_mobile || null,
    });
  });

  useRealtimeCallback(REALTIME_EVENTS.VISIT_STATUS_CHANGED, (event) => {
    if (event.source === 'local') return;
    const { newStatus, visit, nextInQueue, doctorMemberId, doctorName } = event.payload || {};
    if (newStatus !== 'COMPLETED' || !doctorMemberId) return;

    if (nextInQueue) {
      unconfirmVisit(nextInQueue.visit_id);
      addRealtimeBanner(doctorMemberId, {
        type: 'ready',
        doctorName: doctorName || visit?.doctor_name || 'Doctor',
        doctorMemberId,
        nextPatient: nextInQueue.patient_name || 'Next Patient',
        nextQueueNumber: nextInQueue.queue_number,
        nextAppointmentNumber: nextInQueue.appointment_number || null,
        nextVisitId: nextInQueue.visit_id,
        nextPatientMobile: nextInQueue.patient_mobile || null,
      });
    } else {
      dismissRealtimeBanner(doctorMemberId);
    }
  });

  useRealtimeCallback(REALTIME_EVENTS.VISIT_SKIPPED, (event) => {
    if (event.source === 'local') return;
    const { visit, nextInQueue, doctorMemberId, doctorName } = event.payload || {};
    if (!doctorMemberId) return;

    if (nextInQueue) {
      unconfirmVisit(nextInQueue.visit_id);
      addRealtimeBanner(doctorMemberId, {
        type: 'ready',
        doctorName: doctorName || visit?.doctor_name || 'Doctor',
        doctorMemberId,
        skippedPatient: visit?.patient_name,
        nextPatient: nextInQueue.patient_name || 'Next Patient',
        nextQueueNumber: nextInQueue.queue_number,
        nextAppointmentNumber: nextInQueue.appointment_number || null,
        nextVisitId: nextInQueue.visit_id,
        nextPatientMobile: nextInQueue.patient_mobile || null,
      });
    } else {
      dismissRealtimeBanner(doctorMemberId);
    }
  });

  if (finalBanners.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
      {finalBanners.map(([doctorId, banner]) => (
        <BannerCard
          key={doctorId}
          banner={banner}
          isDark={isDark}
          onDismiss={() => confirmSent(doctorId, banner.nextVisitId)}
          onSkip={() => {
            if (banner.nextVisitId && onSkip) {
              onSkip(banner.nextVisitId);
              confirmSent(doctorId, banner.nextVisitId);
            }
          }}
          onCancel={() => {
            if (banner.nextVisitId && onCancel) {
              onCancel(banner.nextVisitId);
            }
          }}
        />
      ))}
    </div>
  );
}


function BannerCard({ banner, isDark, onDismiss, onSkip, onCancel }) {
  const isReady = banner.type === 'ready';

  const bgGradient = isReady
    ? isDark
      ? 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(5,150,105,0.1))'
      : 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(5,150,105,0.04))'
    : isDark
      ? 'linear-gradient(135deg, rgba(59,130,246,0.12), rgba(37,99,235,0.08))'
      : 'linear-gradient(135deg, rgba(59,130,246,0.06), rgba(37,99,235,0.03))';

  const borderColor = isReady
    ? isDark ? 'rgba(16,185,129,0.3)' : 'rgba(16,185,129,0.2)'
    : isDark ? 'rgba(59,130,246,0.25)' : 'rgba(59,130,246,0.15)';

  const accentColor = isReady ? '#10b981' : '#3b82f6';
  const icon = isReady ? '🔔' : '👀';

  return (
    <div
      style={{
        borderRadius: '0.625rem',
        border: `1.5px solid ${borderColor}`,
        background: bgGradient,
        animation: 'ak-banner-in 0.3s ease-out',
        overflow: 'hidden',
      }}
    >
      {/* Main row: icon + message + phone */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.625rem',
        padding: '0.625rem 0.75rem',
      }}>
        {/* Icon */}
        <span style={{
          fontSize: '1.125rem',
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '2rem',
          height: '2rem',
          borderRadius: '0.5rem',
          background: isDark ? `${accentColor}22` : `${accentColor}15`,
        }}>
          {icon}
        </span>

        {/* Message */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            margin: 0,
            fontSize: '0.75rem',
            fontWeight: 600,
            color: isDark ? '#f5f5f7' : '#111827',
            lineHeight: 1.3,
          }}>
            {isReady
              ? `Dr. ${banner.doctorName} is ready for next patient`
              : `Dr. ${banner.doctorName} started seeing ${banner.currentPatient}`
            }
          </p>
          <p style={{
            margin: '0.125rem 0 0',
            fontSize: '0.6875rem',
            color: isDark ? '#9ca3af' : '#6b7280',
            lineHeight: 1.3,
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
          }}>
            {isReady ? 'Send: ' : 'Next up: '}
            <span style={{ fontWeight: 600, color: accentColor }}>
              {banner.nextPatient}
            </span>
            {banner.nextQueueNumber != null && (
              <span> ({formatToken(banner.nextQueueNumber, banner.nextAppointmentNumber)})</span>
            )}
            {banner.nextAppointmentNumber && (
              <span style={{ fontSize: '0.5625rem', padding: '0.0625rem 0.25rem', borderRadius: '0.1875rem', background: isDark ? 'rgba(168,85,247,0.15)' : 'rgba(168,85,247,0.1)', color: '#a855f7', fontWeight: 600, marginLeft: '0.25rem' }}>📅 Appt #{banner.nextAppointmentNumber}</span>
            )}
            {banner.nextPatientMobile && (
              <PhoneButton phoneNumber={banner.nextPatientMobile} isDark={isDark} size="sm" />
            )}
            {banner.skippedPatient && (
              <span style={{ marginLeft: '0.375rem', fontSize: '0.625rem', color: isDark ? '#f59e0b' : '#d97706' }}>
                · {banner.skippedPatient} was skipped
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Action bar */}
      <div style={{
        display: 'flex',
        gap: '0.375rem',
        padding: '0.375rem 0.75rem 0.5rem',
        borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
        flexWrap: 'wrap',
      }}>
        <button
          onClick={onDismiss}
          style={{
            cursor: 'pointer',
            padding: '0.3125rem 0.5rem',
            borderRadius: '0.375rem',
            border: `1px solid ${isDark ? 'rgba(16,185,129,0.25)' : 'rgba(16,185,129,0.2)'}`,
            background: isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.08)',
            fontSize: '0.625rem',
            fontWeight: 600,
            color: '#10b981',
            whiteSpace: 'nowrap',
            transition: 'background 0.15s',
          }}
        >
          ✓ Confirm Sent
        </button>
        <button
          onClick={onSkip}
          style={{
            cursor: 'pointer',
            padding: '0.3125rem 0.5rem',
            borderRadius: '0.375rem',
            border: `1px solid ${isDark ? 'rgba(245,158,11,0.25)' : 'rgba(245,158,11,0.2)'}`,
            background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.06)',
            fontSize: '0.625rem',
            fontWeight: 600,
            color: isDark ? '#f59e0b' : '#d97706',
            whiteSpace: 'nowrap',
            transition: 'background 0.15s',
          }}
        >
          ⏭ Skip — Not Present
        </button>
        {onCancel && (
          <button
            onClick={onCancel}
            style={{
              cursor: 'pointer',
              padding: '0.3125rem 0.5rem',
              borderRadius: '0.375rem',
              border: `1px solid ${isDark ? 'rgba(107,114,128,0.25)' : 'rgba(107,114,128,0.2)'}`,
              background: isDark ? 'rgba(107,114,128,0.1)' : 'rgba(107,114,128,0.06)',
              fontSize: '0.625rem',
              fontWeight: 600,
              color: isDark ? '#9ca3af' : '#6b7280',
              whiteSpace: 'nowrap',
              transition: 'background 0.15s',
            }}
          >
            ✕ Cancel Visit
          </button>
        )}
      </div>
    </div>
  );
}
